/** A file attached to a Doctor:Robotín message */
export interface ChatAttachment {
  id: string
  name: string
  mime_type: string
  /** Bytes that travel to the server (after compression) */
  size: number
  /** base64, no `data:` prefix. Only lives in memory: gone after a page reload. */
  data?: string
  /** Small JPEG data URL of an image. Survives a reload, so the bubble keeps its preview. */
  thumb?: string
}

// Mirrors ATTACHMENT_LIMITS in apps/api/src/services/doctor-robotin.ts
export const ATTACHMENT_LIMITS = {
  maxFiles: 3,
  maxBytes: 4 * 1024 * 1024,
  maxTotalBytes: 10 * 1024 * 1024
}

const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp']
export const ATTACHMENT_ACCEPT = [...IMAGE_TYPES, 'application/pdf'].join(',')

/** Sent when someone attaches a file and writes nothing */
export const DEFAULT_ATTACHMENT_QUESTION = '¿Qué medicamento es y lo tenemos en el inventario?'

export const isImage = (attachment: Pick<ChatAttachment, 'mime_type'>) => attachment.mime_type.startsWith('image/')

export function formatBytes(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function readAsDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result))
    reader.onerror = () => reject(new Error('No se pudo leer el archivo'))
    reader.readAsDataURL(blob)
  })
}

const base64Of = (dataUrl: string) => dataUrl.slice(dataUrl.indexOf(',') + 1)

/** Decode a picture with the browser, which also applies the rotation a phone stores in EXIF */
async function decodeImage(file: Blob): Promise<HTMLImageElement> {
  const url = URL.createObjectURL(file)
  try {
    const image = new Image()
    image.src = url
    await image.decode()
    return image
  } finally {
    URL.revokeObjectURL(url)
  }
}

/** Scale a picture down to `maxSide` and encode it as JPEG. Never scales up. */
function toJpeg(image: HTMLImageElement, maxSide: number, quality: number): Promise<Blob> {
  const scale = Math.min(1, maxSide / Math.max(image.naturalWidth, image.naturalHeight))
  const canvas = document.createElement('canvas')
  canvas.width = Math.max(1, Math.round(image.naturalWidth * scale))
  canvas.height = Math.max(1, Math.round(image.naturalHeight * scale))

  const context = canvas.getContext('2d')
  if (!context) return Promise.reject(new Error('canvas'))
  // JPEG has no transparency: without this a transparent PNG would turn black
  context.fillStyle = '#ffffff'
  context.fillRect(0, 0, canvas.width, canvas.height)
  context.drawImage(image, 0, 0, canvas.width, canvas.height)

  return new Promise((resolve, reject) =>
    canvas.toBlob(blob => (blob ? resolve(blob) : reject(new Error('toBlob'))), 'image/jpeg', quality)
  )
}

/**
 * Get a file ready to send: photos are shrunk (a phone photo is 3-8 MB, a label
 * reads fine at a fraction of that) and get a thumbnail; PDFs go as they are.
 * Throws an Error whose message can be shown to the person.
 */
export async function prepareAttachment(file: File): Promise<ChatAttachment> {
  const id = `adj-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
  const name = file.name || (file.type === 'application/pdf' ? 'documento.pdf' : 'imagen')

  if (file.type === 'application/pdf') {
    if (file.size > ATTACHMENT_LIMITS.maxBytes) {
      throw new Error(`"${name}" pesa ${formatBytes(file.size)}. El máximo por PDF es 4 MB.`)
    }
    return { id, name, mime_type: file.type, size: file.size, data: base64Of(await readAsDataUrl(file)) }
  }

  if (!IMAGE_TYPES.includes(file.type)) {
    throw new Error(`"${name}" no se puede adjuntar. Usa una imagen JPG, PNG o WebP, o un PDF.`)
  }

  let image: Blob
  let thumb: Blob
  try {
    const decoded = await decodeImage(file)
    image = await toJpeg(decoded, 1800, 0.85)
    // A very detailed photo can still be heavy at that quality
    if (image.size > 1024 * 1024) image = await toJpeg(decoded, 1800, 0.7)
    thumb = await toJpeg(decoded, 256, 0.7)
  } catch {
    throw new Error(`No se pudo abrir "${name}". Prueba con otra foto.`)
  }

  if (image.size > ATTACHMENT_LIMITS.maxBytes) {
    throw new Error(`"${name}" sigue pesando demasiado. Prueba con una foto más pequeña.`)
  }

  return {
    id,
    name,
    mime_type: 'image/jpeg',
    size: image.size,
    data: base64Of(await readAsDataUrl(image)),
    thumb: await readAsDataUrl(thumb)
  }
}

/** Something to put in an <img>: the full image while it is in memory, else the thumbnail */
export function imageSource(attachment: ChatAttachment): string | undefined {
  if (!isImage(attachment)) return undefined
  return attachment.data ? `data:${attachment.mime_type};base64,${attachment.data}` : attachment.thumb
}

/** Open a PDF in a new tab. Returns false when the file is no longer in memory. */
export function openPdf(attachment: ChatAttachment): boolean {
  if (!attachment.data) return false
  const bytes = Uint8Array.from(atob(attachment.data), char => char.charCodeAt(0))
  const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }))
  window.open(url, '_blank', 'noopener')
  // The new tab needs a moment to load it before the URL can go
  setTimeout(() => URL.revokeObjectURL(url), 60_000)
  return true
}

interface WithAttachments {
  role: 'user' | 'assistant'
  content: string
  attachments?: ChatAttachment[]
  seen_in_files?: string
}

/** What sessionStorage keeps: everything but the files themselves (it holds about 5 MB) */
export function withoutFileData<T extends WithAttachments>(messages: T[]): T[] {
  return messages.map(message =>
    message.attachments
      ? { ...message, attachments: message.attachments.map(attachment => ({ ...attachment, data: undefined })) }
      : message
  )
}

/**
 * The conversation as the API needs it. Only the newest message with files keeps
 * them, so a follow-up can still be about that photo, and not even that one when
 * the new message brings files of its own.
 */
export function historyForApi(messages: WithAttachments[], sendingFiles: boolean) {
  const recent = messages.slice(-20)
  let keepAt = -1
  if (!sendingFiles) {
    for (let index = recent.length - 1; index >= 0; index--) {
      if (recent[index].role === 'user' && recent[index].attachments?.some(file => file.data)) {
        keepAt = index
        break
      }
    }
  }

  return recent.map((message, index) => ({
    role: message.role,
    content: message.content,
    seen_in_files: message.seen_in_files,
    attachments: index === keepAt ? forApi(message.attachments ?? []) : undefined
  }))
}

export const forApi = (attachments: ChatAttachment[]) =>
  attachments
    .filter(file => file.data)
    .map(file => ({ mime_type: file.mime_type, data: file.data as string, name: file.name }))
